/**
 * Is the register online? (T07-33, design/05 §4) — three signals, one answer the whole app uses:
 *
 * | signal | how |
 * |---|---|
 * | `navigator.onLine` | the tablet has no network at all (Wi-Fi off) |
 * | heartbeat | `GET /pos/registers/current` every 15 s — the shop's server answers (a Wi-Fi without internet still fails here) |
 * | live stream | the realtime client (`offline` when it cannot reach the server either) |
 *
 * → `online` (green 「オンライン」), `offline_short` (yellow 「オフライン (現金のみ)」 — less than
 * `offlineMaxMinutes`, 5, since the connection was lost) or `offline_long` (red 「オフライン 5分超 —
 * 1点物は販売不可」). The moment the connection was lost is kept in IndexedDB (a restart of the app
 * during an outage does not reset the 5 minutes).
 */
import { createContext, useContext, useSyncExternalStore } from "react";

import { readSetting, writeSetting, type PosDatabase } from "@/lib/db";
import { isApiError } from "@/lib/errors";

export type Connectivity = "online" | "offline_short" | "offline_long";

export const HEARTBEAT_MS = 15_000;
export const OFFLINE_SINCE_KEY = "offlineSince";

export interface ConnectivitySnapshot {
  state: Connectivity;
  /** Epoch ms the connection was lost (null while online). */
  offlineSince: number | null;
  /** Last successful answer of the server. */
  lastOkAt: number | null;
}

export interface ConnectivityOptions {
  db: PosDatabase;
  /** One heartbeat: resolves when the server answered (any 2xx/4xx), rejects when unreachable. */
  heartbeat: () => Promise<unknown>;
  now?: () => number;
  /** Minutes after which one-of-a-kind cards stop selling (register rule, default 5). */
  maxMinutes?: () => number;
  intervalMs?: number;
  navigatorOnline?: () => boolean;
}

/** A failed heartbeat means "offline" only when the server could not be reached. */
export function isUnreachable(error: unknown): boolean {
  if (isApiError(error, "network")) {
    return true;
  }
  return (
    isApiError(error) && (error.status === 502 || error.status === 503 || error.status === 504)
  );
}

export function classify(
  offlineSince: number | null,
  now: number,
  maxMinutes: number,
): Connectivity {
  if (offlineSince === null) {
    return "online";
  }
  return now - offlineSince > maxMinutes * 60_000 ? "offline_long" : "offline_short";
}

export class ConnectivityMonitor {
  private snapshot: ConnectivitySnapshot = { state: "online", offlineSince: null, lastOkAt: null };
  private readonly listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;
  private realtimeOffline = false;
  private readonly options: Required<Omit<ConnectivityOptions, "db" | "heartbeat">> & {
    db: PosDatabase;
    heartbeat: () => Promise<unknown>;
  };
  private running = false;
  private beating: Promise<void> | null = null;

  constructor(options: ConnectivityOptions) {
    this.options = {
      now: Date.now,
      maxMinutes: () => 5,
      intervalMs: HEARTBEAT_MS,
      navigatorOnline: () => (typeof navigator === "undefined" ? true : navigator.onLine),
      ...options,
    };
  }

  /** The register's rule (`offlineMaxMinutes`) — the colour follows at once. */
  setMaxMinutes(minutes: number): void {
    this.options.maxMinutes = () => minutes;
    this.recompute(this.snapshot.offlineSince);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): ConnectivitySnapshot => this.snapshot;

  private emit(next: ConnectivitySnapshot): void {
    const prev = this.snapshot;
    if (
      prev.state === next.state &&
      prev.offlineSince === next.offlineSince &&
      prev.lastOkAt === next.lastOkAt
    ) {
      return;
    }
    this.snapshot = next;
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  private recompute(offlineSince: number | null, lastOkAt = this.snapshot.lastOkAt): void {
    this.emit({
      state: classify(offlineSince, this.options.now(), this.options.maxMinutes()),
      offlineSince,
      lastOkAt,
    });
  }

  async start(): Promise<void> {
    if (this.running) {
      return;
    }
    this.running = true;
    const stored = await readSetting<number | null>(this.options.db, OFFLINE_SINCE_KEY, null);
    if (stored !== null) {
      this.recompute(stored);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.onBrowserOnline);
      window.addEventListener("offline", this.onBrowserOffline);
    }
    if (!this.options.navigatorOnline()) {
      await this.markOffline();
    }
    void this.beat();
    this.timer = setInterval(() => void this.beat(), this.options.intervalMs);
    // the colour changes from yellow to red by the clock alone
    this.tick = setInterval(() => this.recompute(this.snapshot.offlineSince), 5_000);
  }

  stop(): void {
    this.running = false;
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (this.tick) {
      clearInterval(this.tick);
    }
    this.timer = null;
    this.tick = null;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.onBrowserOnline);
      window.removeEventListener("offline", this.onBrowserOffline);
    }
  }

  private onBrowserOnline = (): void => {
    void this.beat();
  };

  private onBrowserOffline = (): void => {
    void this.markOffline();
  };

  /** The live stream gave up (`offline`) / came back — counts as a signal, never alone as online. */
  setRealtimeOffline(offline: boolean): void {
    if (offline === this.realtimeOffline) {
      return;
    }
    this.realtimeOffline = offline;
    if (offline) {
      void this.beat();
    }
  }

  /** Ask the server now (「今すぐ確認」, the browser says online again). */
  beat(): Promise<void> {
    this.beating ??= this.runBeat().finally(() => {
      this.beating = null;
    });
    return this.beating;
  }

  private async runBeat(): Promise<void> {
    if (!this.options.navigatorOnline()) {
      await this.markOffline();
      return;
    }
    try {
      await this.options.heartbeat();
      await this.markOnline();
    } catch (error) {
      if (isUnreachable(error)) {
        await this.markOffline();
      } else {
        // the server answered (401 / 404 …): it is reachable
        await this.markOnline();
      }
    }
  }

  async markOffline(): Promise<void> {
    if (this.snapshot.offlineSince !== null) {
      this.recompute(this.snapshot.offlineSince);
      return;
    }
    const since = this.options.now();
    await writeSetting(this.options.db, OFFLINE_SINCE_KEY, since);
    this.recompute(since);
  }

  async markOnline(): Promise<void> {
    if (this.snapshot.offlineSince !== null) {
      await writeSetting(this.options.db, OFFLINE_SINCE_KEY, null);
    }
    this.recompute(null, this.options.now());
  }
}

const ConnectivityContext = createContext<ConnectivityMonitor | null>(null);

export const ConnectivityProvider = ConnectivityContext.Provider;

const alwaysOnline: ConnectivitySnapshot = { state: "online", offlineSince: null, lastOkAt: null };
const noop = () => () => undefined;

/** The register's connection state (online outside a provider — unit tests of single screens). */
export function useConnectivity(): ConnectivitySnapshot {
  const monitor = useContext(ConnectivityContext);
  return useSyncExternalStore(
    monitor?.subscribe ?? noop,
    monitor?.getSnapshot ?? (() => alwaysOnline),
    () => alwaysOnline,
  );
}

export function useConnectivityMonitor(): ConnectivityMonitor | null {
  return useContext(ConnectivityContext);
}
